import { useEffect, useState, useCallback } from 'react'
import { useAppContext } from '@/components/AppContext'
import { CreateStore, ChadhavaOrderV2, SendNotification } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import styled from 'styled-components'
import {
    Card,
    Col,
    Input,
    Row,
    Select,
    Space,
    message
} from 'antd'

import { useSearchParams } from 'react-router-dom'
import { KaggUserPropertyPayload } from '@a4b/api/src/modules/Kagg/type'
import { openErrorNotification } from '@/utils'
import OrderListChadhava from '../../components/OrderListChadhava'
import { orderStatusFilterOptions } from '../../utils/helper'

const { Search } = Input
const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  margin-top: 60px;

  .ant-card-body {
    flex-grow: 1;
  }
  .ant-card-head-wrapper {
    display: block;
    width: 100%;
  }
  .ant-card-head-title {
    flex-basis: 30%;
    padding-bottom: 0px;
  }
  .ant-card-extra {
    flex-basis: 70%;
  }
`

export interface FiltersType {
    serviceType: string
    orderStatus: string | undefined
    bookingId: string
    contactNumber: string
    userId: string
    storeId: string | undefined
    bookingWhatsAppNumber: string
    bookingMemberData: string
    deliveryStatus: string
    bookingAddress: string
}

const ChadhavaOrders = () => {
    const { networkInstance, monetisationRoles, userProfile, app } = useAppContext()
    const { monetisationOfferingsApi, kagg } = networkInstance.clientWithHeaders
    const [orderData, setOrderData] = useState<ChadhavaOrderV2[]>()
    const [messageApi, contextHolder] = message.useMessage()
    const [loading, setLoading] = useState<boolean | null>(null)
    const [pageNumber, setPageNumber] = useState<number | null>(1)
    const [isLastPage, setIsLastPage] = useState<boolean>(false)
    const [userId, setUserId] = useState<string>('');
    const [storeList, setStoreList] = useState<CreateStore[]>([])
    const [isUserPropLoading, setIsUserPropLoading] = useState(false)
    const [searchParams] = useSearchParams()
    const [filters, setFilters] = useState<FiltersType>({
        serviceType: 'offerings',
        orderStatus: undefined,
        bookingId: '',
        contactNumber: '',
        userId: '',
        bookingWhatsAppNumber: '',
        bookingMemberData: '',
        storeId: undefined,
        deliveryStatus: '',
        bookingAddress: '',
    })


    const getChadhavaOrders = useCallback(async () => {
        setLoading(true)
        try {
            const orderResponse = await monetisationOfferingsApi.orderApi.getOrdersV2(
                String(pageNumber),
                filters.orderStatus || '',
                filters.serviceType,
                filters.bookingId,
                filters.contactNumber,
                filters.userId,
                filters.storeId!,
                filters.bookingWhatsAppNumber,
                filters.bookingMemberData,
                filters.deliveryStatus,
                filters.bookingAddress
            )
            setIsLastPage(orderResponse?.data?.data?.is_last_page)
            setOrderData(orderResponse?.data?.data?.orders)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setPageNumber(null)
            console.log("Error: ", error)
        }
    }, [filters?.orderStatus, filters.bookingId, filters.contactNumber, filters.userId, monetisationOfferingsApi.orderApi, pageNumber])

    useEffect(() => {
        // Only call getChadhavaOrders when specific filters have values
        if (filters.bookingId || filters.userId || filters.contactNumber || filters.orderStatus) {
            getChadhavaOrders()
        }
    }, [getChadhavaOrders, filters.bookingId, filters.userId, filters.contactNumber, filters.orderStatus])

    const handleTableChange = (type: string) => {
        if (type === 'previous' && pageNumber! > 1) {
            setPageNumber(pageNumber! - 1)
        } else if (type === 'next' && !isLastPage) {
            setPageNumber(pageNumber! + 1)
        }
    }

    const filterOrders = (type: string, value: string) => {
        setPageNumber(1)
        setFilters((prevFilters: FiltersType) => ({
            ...prevFilters,
            [type]: value
        }))
    }

    const handleRefund = (value: any) => {
    }

    const fetchUserIdByContact = async (contactNumber: string) => {
        try {
            setIsUserPropLoading(true)
            const payload: KaggUserPropertyPayload = {
                contact_numbers: [contactNumber]
            }
            const response = await kagg.getUserData(payload)
            return response?.data?.data?.users?.[0]?.user_id
        } catch (error) {
            openErrorNotification(error)
            return
        } finally {
            setIsUserPropLoading(false)
        }
    }

    const validateContactNumber = (value: string): string | null => {
        if (value) {
            if (value.length !== 10 || isNaN(Number(value))) {
                message.error('Please enter a valid 10-digit contact number.')
                return null
            }
            return `+91${value}`

        }
        return null
    }

    const handleContactSearch = async (value: string) => {
        if (!value) {
            setFilters(prev => ({
                ...prev,
                userId: '',
            }))
            setUserId('')
            return
        }

        const formattedValue = validateContactNumber(value)
        if (!formattedValue) { return }

        const userId = await fetchUserIdByContact(formattedValue)
        if (!userId) {
            message.warning('Contact number not found.');
            return
        }

        setFilters(prev => ({
            ...prev,
            userId: userId,
        }))
        setUserId(userId)
    }

    return (
        <Container
            title='Manage Orders'
            style={{ width: '100%', display: 'flex', flexDirection: 'column', marginTop: '0px' }}
            extra={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Row style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                        <Search
                            placeholder='Booking Id'
                            style={{ width: '12%', marginRight: '10px' }}
                            onSearch={(value: string) => {
                                filterOrders('bookingId', value)
                            }}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    filterOrders('bookingId', '')
                                }
                            }}
                            allowClear
                        />
                        <Search
                            placeholder='User Id'
                            style={{ width: '12%', marginRight: '10px' }}
                            value={userId}
                            onChange={(e) => {
                                setUserId(e?.target?.value)
                                if (!e.target.value) {
                                    filterOrders('userId', '')
                                }
                            }}
                            onSearch={(value: string) => {
                                filterOrders('userId', value)
                            }}
                            allowClear
                        />
                        <Search
                            placeholder='Contact Number'
                            style={{ width: '20%', marginRight: '10px' }}
                            addonBefore='+91'
                            maxLength={10}
                            minLength={10}
                            inputMode='numeric'
                            onSearch={handleContactSearch}
                            allowClear
                            loading={isUserPropLoading}
                        />

                        <Select
                            size='middle'
                            placeholder="Order Status"
                            style={{ width: '11%', marginRight: '10px' }}
                            value={filters?.orderStatus}
                            options={orderStatusFilterOptions.map((item: any) => ({
                                label: item.label,
                                value: item.value
                            }))}
                            onChange={(value: string) => {
                                filterOrders('orderStatus', value)
                            }}
                        />
                    </Row>
                </div>
            }
        >
            <Space direction={'vertical'} style={{ width: '100%' }}></Space>
            <Row>
                <Col span={28}>
                    {contextHolder}
                    <OrderListChadhava
                        orderData={orderData}
                        handleTableChange={handleTableChange}
                        filters={filters}
                        refundHandler={handleRefund}
                        userRole={monetisationRoles?.user_type!}
                        loading={loading || false}
                        currentPage={pageNumber}
                        onRefundSuccess={getChadhavaOrders}
                    />
                </Col>
            </Row>
        </Container >
    )
}

export default ChadhavaOrders
